import React from 'react';
import {
  AvailableThemeOverridesStrict,
  ThemeProviderProps,
  ThemeProvider,
  VariableColorKeys,
  ThemeContainer,
} from '@dropbox/dig-foundations';
import { HexColor } from 'signer-app/white-labeling/types';
import { useBundleProps } from 'signer-app/bundle-props';
import styles from './theme-provider.module.css';

type ColorOverides = {
  [K in VariableColorKeys]?: HexColor;
};

type Props = Omit<ThemeProviderProps, 'overrides'> & {
  colors?: 'reset';
};

/**
 * This is a convenience wrapper around DIG's `ThemeProvider` that allows you to
 * pass color theme overrides without needing to specify both `bright` and `dark`
 * modes. White-labeling only supplies one set of colors.
 */
export function WhiteLabelThemeProvider({ colors, children, ...rest }: Props) {
  const { whiteLabelStyles, hasCustomStyles } = useBundleProps();
  const isResetting = colors === 'reset';
  const applyCustomStyles = hasCustomStyles && !isResetting;

  const overrides = React.useMemo(() => {
    if (isResetting) {
      return 'reset';
    }

    const colorOverrides: ColorOverides | undefined = hasCustomStyles
      ? {
          'Background Subtle': whiteLabelStyles.headerBackgroundColor,
          'Opacity Surface - State 1':
            whiteLabelStyles.secondaryButtonColorHover,
          'Opacity Surface': whiteLabelStyles.secondaryButtonColor,

          'Primary CTA Base - State 1':
            whiteLabelStyles.primaryButtonColorHover,
          'Primary CTA Base': whiteLabelStyles.primaryButtonColor,
          'Primary CTA On Base': whiteLabelStyles.primaryButtonTextColor,

          'Selection Base - State 1': whiteLabelStyles.primaryButtonColorHover,
          'Selection Base': whiteLabelStyles.primaryButtonColor,
          'Selection On Base': whiteLabelStyles.primaryButtonTextColor,

          'Text Base': whiteLabelStyles.textColor1,
          'Text Subtle': whiteLabelStyles.textColor2,
        }
      : undefined;

    return colorOverrides ? createOverrides(colorOverrides) : undefined;
  }, [hasCustomStyles, whiteLabelStyles, isResetting]);

  const customOverrides = React.useMemo(() => {
    return applyCustomStyles
      ? {
          '--custom-secondary-button-text-color-hover':
            whiteLabelStyles.secondaryButtonTextColorHover,
          '--custom-secondary-button-background-color-hover':
            whiteLabelStyles.secondaryButtonColorHover,
          '--custom-secondary-button-text-color':
            whiteLabelStyles.secondaryButtonTextColor,
          '--custom-secondary-button-background-color':
            whiteLabelStyles.secondaryButtonColor,
          '--custom-primary-button-text-color-hover':
            whiteLabelStyles.primaryButtonTextColorHover,
          '--custom-link-color': whiteLabelStyles.linkColor,
          '--custom-loader-background-color':
            whiteLabelStyles.pageBackgroundColor,
          '--custom-text-color-1': whiteLabelStyles.textColor1,
          '--custom-header-background-color':
            whiteLabelStyles.headerBackgroundColor,
        }
      : undefined;
  }, [applyCustomStyles, whiteLabelStyles]);

  return (
    <ThemeProvider theme="vis2023" {...rest} overrides={overrides}>
      <ThemeContainer>
        <span
          className={applyCustomStyles ? styles.customStyles : undefined}
          style={customOverrides}
        >
          {children}
        </span>
      </ThemeContainer>
    </ThemeProvider>
  );
}

function createOverrides(colors: ColorOverides): AvailableThemeOverridesStrict {
  const overrideColors: AvailableThemeOverridesStrict['colors'] = {};

  Object.keys(colors).forEach((key) => {
    const colorKey = key as VariableColorKeys;
    const hexColor = colors[colorKey]!;
    if (!hexColor) {
      return;
    }
    overrideColors[colorKey] = {
      dark: hexColor,
      bright: hexColor,
    };
  });

  return {
    colors: overrideColors,
  };
}
